// One job: its details, its status, its bookings, its paperwork.
//
// Tabbed rather than one long stack (Zanetto Tools' job page, Nathan's ask):
// four panels down a page means the schedule is below the fold on a phone and
// the documents below that. The strip is the same one a person's page uses —
// the app has one tab idiom, and `?tab=` makes each half linkable.
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  scheduling,
  type Booking,
  type ClientRep,
  type Job,
  type JobNote,
  type JobStatus,
} from '../schedulingApi';
import { ApiError } from '../api';
import { dateRangeLabel, formatDate } from '../lib/dates';
import { notifications } from '../notificationsApi';
import { NotesPanel } from '../components/NotesPanel';
import { RepModal } from '../components/clients/RepsPanel';
import { Banner, Field } from '../ui';
import { PageHeader } from '../components/ui/Page';
import { CardList, RowCard } from '../components/ui/RowCard';
import { Modal } from '../components/ui/Modal';
import { JOB_STATUS_COLOURS, StatusSelect } from '../components/ui/StatusSelect';
import { STATUS_LABEL } from './JobsPage';
import { Documents } from '../components/Documents';
import { PurchaseOrderFile } from '../components/jobs/PurchaseOrderFile';
import { InvoicingPanel, JobValuePanel, VariationsPanel } from '../components/jobs/JobMoneyTabs';
import type { JobMoneyBlock } from '../jobMoneyApi';
import { useOptionalSession } from '../session';
import type { Attachment } from '../documentsApi';
import { CLOSED_STATUSES } from '../lib/jobStatus';
import { LinkedSubmissions } from '../components/forms/LinkedSubmissions';
import { useTabParam } from '../lib/useTabParam';

type BookingWithResource = Booking & { resourceName: string };

const JOB_TABS = [
  { key: 'details', label: 'Details' },
  // Round 45: Zanetto Tools' money tabs, right after Details as ZT has them.
  { key: 'value', label: 'Job value' },
  { key: 'variations', label: 'Variations' },
  { key: 'invoicing', label: 'Invoicing' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'rep', label: 'Representative' },
  { key: 'documents', label: 'Documents' },
  { key: 'forms', label: 'Forms' },
  { key: 'notes', label: 'Internal notes' },
] as const;

const JOB_TAB_KEYS = JOB_TABS.map((t) => t.key);

/**
 * The statuses a person may CHOOSE.
 *
 * `scheduled` is not among them and must not be: it is derived from whether
 * the job has bookings (`syncJobScheduledStatus` on the server), so offering
 * it would be offering a choice the server immediately overrules. It still
 * shows on the trigger when it is the current status.
 */
const CHOOSABLE: JobStatus[] = [
  'pending',
  'in_progress',
  'on_hold',
  'completed',
  'invoiced',
  'cancelled',
];

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<Job | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [bookings, setBookings] = useState<BookingWithResource[]>([]);
  const [jobManagerName, setJobManagerName] = useState<string | null>(null);
  const [rep, setRep] = useState<ClientRep | null>(null);
  const [fromQuote, setFromQuote] = useState<{ id: string; number: string } | null>(null);
  const [notes, setNotes] = useState<JobNote[]>([]);
  const [poFile, setPoFile] = useState<Attachment | null>(null);
  const [money, setMoney] = useState<JobMoneyBlock | null>(null);
  const user = useOptionalSession()?.user ?? null;
  const isAdmin = user?.role === 'admin';
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useTabParam(JOB_TAB_KEYS);
  const [target, setTarget] = useState<JobStatus | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const result = await scheduling.getJob(id);
      setJob(result.job);
      setClientName(result.clientName);
      setBookings(result.bookings);
      setJobManagerName(result.jobManagerName);
      setRep(result.rep);
      setFromQuote(result.fromQuote ?? null);
      setNotes(result.notes);
      setPoFile(result.purchaseOrderFile ?? null);
      setMoney({ money: result.money, variations: result.variations, invoices: result.invoices });
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that job.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!job) {
    return error ? <Banner kind="error">{error}</Banner> : <p className="muted">Loading…</p>;
  }

  // The server's closed set (lib/jobStatus.ts): invoiced was missing here, so
  // an invoiced job offered an Edit the server then refused (round 45).
  const closed = CLOSED_STATUSES.includes(job.status);

  function beginStatusChange(next: JobStatus) {
    setError(null);
    setNotice(null);
    setTarget(next);
  }

  return (
    <>
      <PageHeader
        title={`${job.number} · ${job.title}`}
        actions={
          <>
            {/* The status is the thing most often changed on this page, so it
                sits in the header where the eye already is, rather than in a
                panel four screens down. */}
            <StatusSelect
              value={job.status}
              options={CHOOSABLE}
              labels={STATUS_LABEL}
              colours={JOB_STATUS_COLOURS}
              label="Status"
              onChange={beginStatusChange}
            />
            <Link to="/app/jobs" className="btn-secondary">
              Back to jobs
            </Link>
          </>
        }
      />

      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200">
        {JOB_TABS.map((section) => {
          const active = tab === section.key;
          return (
            <button
              key={section.key}
              type="button"
              onClick={() => setTab(section.key)}
              aria-current={active ? 'page' : undefined}
              className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
                active
                  ? 'border-brand-700 font-medium text-brand-800'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {section.label}
            </button>
          );
        })}
      </div>

      {target && (
        <StatusConfirm
          job={job}
          target={target}
          onClose={() => setTarget(null)}
          onChanged={(message) => {
            setTarget(null);
            setNotice(message);
            void load();
          }}
          onError={setError}
        />
      )}

      {tab === 'details' && (
      <section className="panel">
        {/* No status badge here. The interactive StatusSelect sits in the page
            header directly above this row, so a read-only chip beside the Edit
            button was the same fact stated twice, a few pixels apart — and the
            one that could not be clicked. Round 34. */}
        <div className="row mb-3">
          <h2 className="!mb-0">Overview</h2>
          <span className="spacer" />
          {!closed && (
            <button className="secondary" onClick={() => setEditing((open) => !open)}>
              {editing ? 'Cancel' : 'Edit'}
            </button>
          )}
        </div>

        {editing ? (
          <EditJobForm
            job={job}
            poFile={poFile}
            onPoFileChanged={setPoFile}
            onSaved={() => {
              setEditing(false);
              void load();
            }}
          />
        ) : (
          <dl className="details">
            <dt>Title</dt>
            <dd>{job.title}</dd>
            {/* Round 43. The quote this job was made from: a link to its price,
                which stays on the quote. This page is manager-only. */}
            {fromQuote && (
              <>
                <dt>Made from quote</dt>
                <dd>
                  <Link className="table-link" to={`/app/quotes/${fromQuote.id}`}>
                    {fromQuote.number}
                  </Link>
                </dd>
              </>
            )}
            <dt>Client</dt>
            <dd>{clientName ?? <span className="muted">Internal work</span>}</dd>
            <dt>Site address</dt>
            <dd>{job.siteAddress ?? <span className="muted">—</span>}</dd>
            {/* The number and the file (round 45). Still omitted when neither
                was given (migration 019's rule): the "Attach a purchase
                order" action under the list adds the file, on a closed job
                too, because paperwork arrives late (spec 02). */}
            {(job.purchaseOrderNumber || poFile) && (
              <>
                <dt>Purchase order</dt>
                <dd>
                  <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
                    {job.purchaseOrderNumber && <span>{job.purchaseOrderNumber}</span>}
                    <PurchaseOrderFile jobId={job.id} file={poFile} onChanged={setPoFile} compact />
                  </div>
                </dd>
              </>
            )}
            {/* The dates the client was told. Shown only when given: a row
                reading "—" on most jobs is noise (migration 019's rule). The
                Schedule tab is still where the real dates live. */}
            {(job.startDate || job.endDate) && (
              <>
                <dt>Dates told to client</dt>
                <dd>
                  {job.startDate && job.endDate
                    ? dateRangeLabel(job.startDate, job.endDate)
                    : formatDate((job.startDate ?? job.endDate) as string)}
                </dd>
              </>
            )}
            {jobManagerName && (
              <>
                <dt>Job manager</dt>
                <dd>{jobManagerName}</dd>
              </>
            )}
            <dt>Description</dt>
            <dd>{job.description ?? <span className="muted">—</span>}</dd>
            {job.cancelReason && (
              <>
                <dt>Cancelled because</dt>
                <dd>{job.cancelReason}</dd>
              </>
            )}
          </dl>
        )}
        {!editing && !job.purchaseOrderNumber && !poFile && (
          <div className="mt-3">
            <PurchaseOrderFile jobId={job.id} file={null} onChanged={setPoFile} compact attachLabel="Attach a purchase order" />
          </div>
        )}
        <p className="hint mt-3">
          Scheduled is worked out automatically from whether the job has bookings, so it is not one
          of the statuses you can pick.
        </p>
      </section>
      )}

      {tab === 'value' && money && (
        <JobValuePanel jobId={job.id} block={money} isAdmin={isAdmin} onChanged={setMoney} />
      )}
      {tab === 'variations' && money && (
        <VariationsPanel jobId={job.id} block={money} canAdd={job.status !== 'cancelled'} onChanged={setMoney} />
      )}
      {tab === 'invoicing' && money && (
        <InvoicingPanel jobId={job.id} block={money} isAdmin={isAdmin} onChanged={setMoney} />
      )}

      {tab === 'schedule' && (
      <section className="panel">
        <h2>Schedule</h2>
        {bookings.length === 0 ? (
          <p className="muted">
            Nothing booked yet. Book this job from the <Link to="/app/calendar">calendar</Link>.
          </p>
        ) : (
          <>
          <CardList>
            {bookings.map((booking) => (
              <RowCard
                key={booking.id}
                title={booking.resourceName}
                badges={
                  booking.inConflict ? (
                    <span className="pill conflict-pill">needs re-planning</span>
                  ) : undefined
                }
              >
                <div>
                  {dateRangeLabel(booking.startDate, booking.endDate)} ·{' '}
                  {booking.startTime ? `${booking.startTime}–${booking.endTime}` : 'all day'}
                </div>
                {booking.note && <div>{booking.note}</div>}
              </RowCard>
            ))}
          </CardList>
          <div className="-mx-6 overflow-x-auto border-y border-gray-200 max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Resource</th>
                  <th>Dates</th>
                  <th>Times</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <tr key={booking.id}>
                    <td className="font-medium">
                      {booking.resourceName}
                      {booking.inConflict && (
                        <span className="pill conflict-pill ml-2">needs re-planning</span>
                      )}
                    </td>
                    <td className="table-muted whitespace-nowrap">
                      {dateRangeLabel(booking.startDate, booking.endDate)}
                    </td>
                    <td className="table-muted whitespace-nowrap">
                      {booking.startTime ? `${booking.startTime}–${booking.endTime}` : 'All day'}
                    </td>
                    <td className="table-muted">{booking.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
        <p className="hint mt-3">
          Each resource keeps its own dates; moving one booking never moves the others.
        </p>
      </section>
      )}

      {/* Attachments stay editable on a completed or cancelled job: paperwork
          arrives late, and that is the one thing a closed job still accepts
          (spec 02). */}
      {tab === 'documents' && (
        <Documents kind="job" ownerId={job.id} title="Documents" />
      )}

      {/* Site checks and reports filled in against this job. Shown even when
          empty here, unlike on a resource page: the tab has already promised
          the reader something is behind it. */}
      {tab === 'rep' && (
        <JobRepPanel
          job={job}
          rep={rep}
          clientName={clientName}
          onChanged={load}
        />
      )}

      {tab === 'forms' && <LinkedSubmissions jobId={job.id} showWhenEmpty heading={null} />}

      {tab === 'notes' && (
        <NotesPanel
          title="Internal notes"
          notes={notes}
          canWrite
          intro="For your team; never shown to the client, and your crew cannot see them."
          onAdd={async (body) => {
            const { note } = await scheduling.addJobNote(job.id, body);
            setNotes((current) => [note, ...current]);
          }}
        />
      )}
    </>
  );
}

function EditJobForm({
  job,
  poFile,
  onPoFileChanged,
  onSaved,
}: {
  job: Job;
  poFile: Attachment | null;
  onPoFileChanged: (file: Attachment | null) => void;
  onSaved: () => void;
}) {
  const [number, setNumber] = useState(job.number);
  const [title, setTitle] = useState(job.title);
  const [siteAddress, setSiteAddress] = useState(job.siteAddress ?? '');
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState(job.purchaseOrderNumber ?? '');
  const [description, setDescription] = useState(job.description ?? '');
  const [startDate, setStartDate] = useState(job.startDate ?? '');
  const [endDate, setEndDate] = useState(job.endDate ?? '');
  const [jobManagerId, setJobManagerId] = useState(job.jobManagerId ?? '');
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  // The people this company could put in charge of a job. `notifications.people`
  // is the manager-callable name list that already exists for the audience
  // picker; the full user list is admin-only and a manager editing a job must
  // not be refused a dropdown they are allowed to use.
  useEffect(() => {
    void notifications
      .people()
      .then((result) => setPeople(result.people))
      // A 403 here is an ordinary outcome, not an error worth showing: the
      // field simply does not offer a picker.
      .catch(() => setPeople([]));
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFields({});
    try {
      await scheduling.updateJob(job.id, {
        // Sent only when it changed — the undefined-means-unchanged rule.
        // Manual numbering exists for the client whose numbering no format
        // fits (round 36); a duplicate is a 409 named after the other job.
        ...(number === job.number ? {} : { number }),
        title,
        siteAddress: siteAddress === '' ? null : siteAddress,
        purchaseOrderNumber: purchaseOrderNumber === '' ? null : purchaseOrderNumber,
        description: description === '' ? null : description,
        startDate: startDate === '' ? null : startDate,
        endDate: endDate === '' ? null : endDate,
        // Only sent when the picker was shown. A manager whose people list
        // 403s gets no dropdown, and a form that cannot show a field must
        // OMIT it rather than send a blank that would clear the manager.
        ...(people.length > 0 ? { jobManagerId: jobManagerId === '' ? null : jobManagerId } : {}),
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not save that.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      {error && <Banner kind="error">{error}</Banner>}
      <div className="row">
        <Field
          label="Job number"
          name="number"
          value={number}
          onChange={setNumber}
          error={fields.number}
          autoCapitalize="characters"
          spellCheck={false}
        />
        <Field label="Title" name="title" value={title} onChange={setTitle} />
      </div>
      <Field
        label="Site address"
        name="siteAddress"
        value={siteAddress}
        onChange={setSiteAddress}
        required={false}
      />
      {/* Round 45: the number takes half the line and the file the other
          half, rather than one input the full width of a desktop screen. */}
      {/* A grid, not .row: .row aligns to the BOTTOM (legacy.css), which
          dropped the number's label below the file's taller zone. */}
      <div className="mb-3 grid grid-cols-1 items-start gap-x-4 sm:grid-cols-2">
        <Field
          label="Purchase order number"
          name="purchaseOrderNumber"
          value={purchaseOrderNumber}
          onChange={setPurchaseOrderNumber}
          required={false}
          autoCapitalize="none"
          spellCheck={false}
        />
        <div className="min-w-0 flex-1">
          <span className="form-label">Purchase order file</span>
          <PurchaseOrderFile jobId={job.id} file={poFile} onChanged={onPoFileChanged} />
        </div>
      </div>
      {/* The dates the CLIENT was told. The Schedule tab is still where the
          real dates are — these do not move a booking and nothing derives
          from them. */}
      {/* `pair`: side by side on a phone too, not a cramped wrap
          (legacy.css has the argument). */}
      <div className="row pair">
        <Field
          label="Start date"
          name="startDate"
          type="date"
          value={startDate}
          onChange={setStartDate}
          error={fields.startDate}
          required={false}
        />
        <Field
          label="End date"
          name="endDate"
          type="date"
          value={endDate}
          onChange={setEndDate}
          error={fields.endDate}
          required={false}
        />
      </div>
      <p className="hint -mt-1">
        What you told the client. The crew&rsquo;s actual days come from the bookings on the
        Schedule tab.
      </p>
      {people.length > 0 && (
        <label htmlFor="jobManagerId">
          Job manager
          <select
            id="jobManagerId"
            value={jobManagerId}
            onChange={(event) => setJobManagerId(event.target.value)}
          >
            <option value="">Nobody</option>
            {people.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <Field
        label="Description"
        name="description"
        value={description}
        onChange={setDescription}
        required={false}
      />
      {/* mt-4: `.row` strips its labels' bottom margin, so a submit after one
          has nothing spacing it. btn-primary must be spelled out here: any
          class on a button defeats legacy.css's `button:not([class])` rule. */}
      <button type="submit" className="btn-primary mt-4" disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}


/**
 * Confirming a status change, including the prompt about bookings still to
 * come.
 *
 * Completing or cancelling a job does not touch its bookings by itself — past
 * bookings are history, and future ones are the manager's call (spec 02). So
 * the choice is put in front of them, with the actual bookings listed.
 *
 * A dialog rather than the inline box it used to be: the trigger now lives in
 * the page header, and a confirmation that appeared three tabs away from what
 * was pressed would go unnoticed.
 */
function StatusConfirm({
  job,
  target,
  onClose,
  onChanged,
  onError,
}: {
  job: Job;
  target: JobStatus;
  onClose: () => void;
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [future, setFuture] = useState<(Booking & { resourceName: string })[]>([]);
  const [release, setRelease] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // On hold asks too (round 45): parking a job is when its booked crew
    // most often needs freeing, though nothing is ticked unless chosen.
    if (target !== 'completed' && target !== 'cancelled' && target !== 'on_hold') {
      setFuture([]);
      return;
    }
    void scheduling.futureBookings(job.id).then(
      (result) => {
        setFuture(result.bookings);
        setRelease(target !== 'on_hold' && result.bookings.length > 0);
      },
      () => setFuture([])
    );
  }, [job.id, target]);

  async function confirm() {
    setBusy(true);
    try {
      await scheduling.setJobStatus(job.id, {
        status: target,
        cancelReason: target === 'cancelled' && reason !== '' ? reason : null,
        releaseFutureBookings: release && future.length > 0,
      });
      onChanged('Status updated.');
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not change the status.');
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Set to ${STATUS_LABEL[target]}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-ghost btn-sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={() => void confirm()}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Confirm'}
          </button>
        </>
      }
    >
      <div className="legacy-page">
        <p>
          Set <strong>{job.number}</strong> to <strong>{STATUS_LABEL[target]}</strong>?
        </p>

        {target === 'cancelled' && (
          <Field
            label="Reason (optional)"
            name="reason"
            value={reason}
            onChange={setReason}
            required={false}
          />
        )}

        {future.length > 0 && (
          <>
            <p>
              This job still has {future.length} booking{future.length === 1 ? '' : 's'} to come:
            </p>
            {/* The BookingModal idiom for a list inside a dialog: bordered,
                divided, capped. Preflight leaves a bare <ul> styleless, which
                is how these rows spent round 34 looking like a paragraph. */}
            <ul className="m-0 max-h-56 list-none divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200 p-0">
              {future.map((booking) => (
                <li
                  key={booking.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span className="font-medium text-gray-800">{booking.resourceName}</span>
                  <span className="whitespace-nowrap text-gray-600">
                    {dateRangeLabel(booking.startDate, booking.endDate)}
                  </span>
                </li>
              ))}
            </ul>
            {/* Its own block with real space above — the tick box sat flush
                against the list and read as part of it. */}
            <label className="checkbox mt-4">
              <input
                type="checkbox"
                checked={release}
                onChange={(e) => setRelease(e.target.checked)}
              />
              Release them and free those resources up
            </label>
            <p className="hint">
              All of them or none of them, and days already worked stay on the record either way.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * The client's representative for this job.
 *
 * Zanetto Tools' flow, with one deliberate difference: ZT also keeps
 * hand-typed rep name/phone/email columns ON THE JOB, so a one-off contact can
 * be entered without saving them anywhere. Here the picker is the only way in
 * and "Add representative" saves to the CLIENT — which means the next job for
 * that client offers them too, and there is one answer to "who do we deal with
 * at Redgum?" instead of one per job. A rep removed later is archived rather
 * than deleted, so this join keeps resolving.
 */
function JobRepPanel({
  job,
  rep,
  clientName,
  onChanged,
}: {
  job: Job;
  rep: ClientRep | null;
  clientName: string | null;
  onChanged: () => Promise<void>;
}) {
  const [reps, setReps] = useState<ClientRep[]>([]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadReps = useCallback(async () => {
    if (!job.clientId) {
      setReps([]);
      return;
    }
    try {
      const { reps: list } = await scheduling.listClientReps(job.clientId);
      setReps(list);
    } catch {
      // The picker is a convenience; the tab still shows whoever is named.
      setReps([]);
    }
  }, [job.clientId]);

  useEffect(() => {
    void loadReps();
  }, [loadReps]);

  async function choose(repId: string) {
    setBusy(true);
    setError(null);
    try {
      await scheduling.updateJob(job.id, { clientRepId: repId === '' ? null : repId });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  // A tab has already promised the reader something is behind it, so it
  // answers even when there is nothing to show (round 33's rule).
  if (!job.clientId) {
    return (
      <section className="panel">
        <h2>Representative</h2>
        <p className="muted">
          This is internal work with no client, so there is nobody at the other end to name. Give
          the job a client first.
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>Representative</h2>
      {error && <Banner kind="error">{error}</Banner>}

      <div className="row mb-3">
        <label htmlFor="job-rep" className="!mb-0">
          Who you deal with at {clientName ?? 'this client'}
          <select
            id="job-rep"
            value={job.clientRepId ?? ''}
            disabled={busy}
            onChange={(event) => void choose(event.target.value)}
          >
            <option value="">Nobody chosen</option>
            {/* A rep removed after this job named them is archived, not
                deleted, so it is still listed here — otherwise the dropdown
                would render blank on a job that plainly has a rep. */}
            {rep?.archived && (
              <option value={rep.id}>{rep.name} (removed)</option>
            )}
            {reps.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-secondary" onClick={() => setAdding(true)}>
          Add representative
        </button>
      </div>

      {rep ? (
        <dl className="details">
          <dt>Name</dt>
          <dd>{rep.name}</dd>
          <dt>Phone</dt>
          <dd>
            {rep.phone ? (
              // Tapped on site, which is the whole reason it is here.
              <a href={`tel:${rep.phone.replace(/[^+\d]/g, '')}`}>{rep.phone}</a>
            ) : (
              <span className="muted">—</span>
            )}
          </dd>
          <dt>Email</dt>
          <dd>{rep.email ?? <span className="muted">—</span>}</dd>
          {rep.notes && (
            <>
              <dt>Notes</dt>
              <dd>{rep.notes}</dd>
            </>
          )}
        </dl>
      ) : (
        <p className="muted">
          Nobody chosen yet. Pick one of {clientName ?? 'the client'}&rsquo;s people, or add
          somebody new; they are saved against the client, so the next job can name them too.
        </p>
      )}

      {adding && (
        <RepModal
          clientId={job.clientId}
          rep={null}
          onClose={() => setAdding(false)}
          onSaved={async (saved) => {
            setAdding(false);
            // Created, then chosen — ZT's flow. Adding somebody without
            // selecting them would mean doing the job twice.
            await scheduling.updateJob(job.id, { clientRepId: saved.id });
            await loadReps();
            await onChanged();
          }}
        />
      )}
    </section>
  );
}
